/**
 * EPMS perio grid addressing — decoded from the Angular clinical module.
 *
 * Four tables (fUpper / lUpper / fLower / lLower). Value elements use
 * `{section}Pd{3*toothIndex + siteIndex}`. Tooth headers use 1–16 upper and
 * 32–17 lower. Site 0 is the left cell (M field name). Pearl columns put
 * distal there on teeth 1–8 and 25–32; 9–24 stay mesial-left.
 */

import type { LivePerioSite } from '../../live-perio/ports';
import { fullArchMirroredSequence } from '../../perio/odontogram';
import { epmsSiteIndex } from './site-index';

export const EPMS_PD_SECTIONS = [
  'fUpper',
  'lUpper',
  'fLower',
  'lLower',
] as const;

export type EpmsPdSection = (typeof EPMS_PD_SECTIONS)[number];

export type EpmsRow = 'Pd' | 'Bld' | 'Fg' | 'Gm' | 'Mgj' | 'Sup' | 'Laa';

/** Wrapping `td` rows that stack into one visual tooth column. */
const TOOTH_COLUMN_ROWS = [
  'Pd',
  'Gm',
  'Cal',
  'Mgj',
  'Fg',
  'Bld',
  'Sup',
  'Laa',
  'Plq',
] as const;

export interface EpmsPdAddress {
  section: EpmsPdSection;
  toothIndex: number;
  siteIndex: number;
  toothNumber: number;
  cellId: string;
  valueId: string;
}

export type EpmsMobSection = 'fUpper' | 'fLower';

export interface EpmsMobAddress {
  section: EpmsMobSection;
  toothIndex: number;
  toothNumber: number;
  cellId: string;
  valueId: string;
}

/** Facial Plq only — the td has the id; button / ul / li are id-less. */
export interface EpmsPlqAddress {
  section: EpmsMobSection;
  toothIndex: number;
  toothNumber: number;
  cellId: string;
}

const FACIAL_SITES: ReadonlySet<string> = new Set(['MB', 'B', 'DB']);
const LINGUAL_SITES: ReadonlySet<string> = new Set(['ML', 'L', 'DL']);

function toothIndexFor(tooth: number): number | null {
  if (tooth >= 1 && tooth <= 16) return tooth - 1;
  if (tooth >= 17 && tooth <= 32) return 32 - tooth;
  return null;
}

function sectionFor(tooth: number, site: string): EpmsPdSection | null {
  const upper = tooth >= 1 && tooth <= 16;
  const lower = tooth >= 17 && tooth <= 32;
  if (upper && FACIAL_SITES.has(site)) return 'fUpper';
  if (upper && LINGUAL_SITES.has(site)) return 'lUpper';
  if (lower && FACIAL_SITES.has(site)) return 'fLower';
  if (lower && LINGUAL_SITES.has(site)) return 'lLower';
  return null;
}

/**
 * Resolves a Pearl tooth + site onto the EPMS PD cell / value ids.
 *
 * @param tooth - Universal tooth number 1–32
 * @param site - Pearl site (MB/B/DB/ML/L/DL)
 * @returns Address or null when the pair is out of range
 */
export function addressEpmsPdCell(
  tooth: number,
  site: LivePerioSite | string,
): EpmsPdAddress | null {
  return addressEpmsCell(tooth, site, 'Pd');
}

/**
 * Facial mobility only — one input per tooth (`fUpperMob13`, not a 3-site flat).
 *
 * @param tooth - Universal tooth number 1–32
 * @returns Address or null when the tooth is out of range
 */
export function addressEpmsMobCell(tooth: number): EpmsMobAddress | null {
  const toothIndex = toothIndexFor(tooth);
  if (toothIndex == null) return null;
  const section: EpmsMobSection = tooth <= 16 ? 'fUpper' : 'fLower';
  return {
    section,
    toothIndex,
    toothNumber: tooth,
    cellId: `${section}Mobs${toothIndex}`,
    valueId: `${section}Mob${toothIndex}`,
  };
}

/**
 * Facial plaque only — one td per tooth (`fUpperPlqs13`, no child ids).
 *
 * @param tooth - Universal tooth number 1–32
 * @returns Address or null when the tooth is out of range
 */
export function addressEpmsPlqCell(tooth: number): EpmsPlqAddress | null {
  const toothIndex = toothIndexFor(tooth);
  if (toothIndex == null) return null;
  const section: EpmsMobSection = tooth <= 16 ? 'fUpper' : 'fLower';
  return {
    section,
    toothIndex,
    toothNumber: tooth,
    cellId: `${section}Plqs${toothIndex}`,
  };
}

/**
 * Same flatIndex grammar as PD for Bld / Fg / Gm / Mgj / Sup / Laa.
 * Site index follows Pearl LTR columns (`epmsSiteIndex`), not spoken order.
 *
 * @param tooth - Universal tooth number 1–32
 * @param site - Pearl site
 * @param row - Value-row prefix
 * @returns Address or null when the pair is out of range
 */
export function addressEpmsCell(
  tooth: number,
  site: LivePerioSite | string,
  row: EpmsRow,
): EpmsPdAddress | null {
  const toothIndex = toothIndexFor(tooth);
  const siteIndex = epmsSiteIndex(tooth, site);
  const section = sectionFor(tooth, site);
  if (toothIndex == null || siteIndex == null || !section) return null;
  const flat = 3 * toothIndex + siteIndex;
  return {
    section,
    toothIndex,
    siteIndex,
    toothNumber: tooth,
    cellId: `${section}${row}s${toothIndex}`,
    valueId: `${section}${row}${flat}`,
  };
}

/**
 * Wrapping cell ids for every metric row of one universal tooth
 * (facial + lingual of that arch). Hidden rows are simply absent in the
 * DOM — callers skip missing nodes. Mobility is facial-only.
 *
 * @param tooth - Universal tooth number 1–32
 * @returns Cell ids, or `[]` when the tooth is out of range
 */
export function listEpmsToothColumnCellIds(tooth: number): string[] {
  const toothIndex = toothIndexFor(tooth);
  if (toothIndex == null) return [];
  const sections: readonly EpmsPdSection[] =
    tooth <= 16 ? ['fUpper', 'lUpper'] : ['fLower', 'lLower'];
  const ids: string[] = [];
  for (const section of sections) {
    for (const row of TOOTH_COLUMN_ROWS) {
      ids.push(`${section}${row}s${toothIndex}`);
    }
  }
  ids.push(`${tooth <= 16 ? 'fUpper' : 'fLower'}Mobs${toothIndex}`);
  return ids;
}

/**
 * Writer walk matching Voice `full_arch_mirrored` (not EPMS auto-advance).
 * Tooth order is 1–16F, 16–1L, 32–17F, 17–32L. Intra-tooth sites mirror
 * at the midline (right facial DB→B→MB, left MB→B→DB). Each slot is still
 * addressed by anatomical site name — never by spoken position.
 *
 * @returns 192 tooth+site slots
 */
export function epmsPdSequence(): { tooth: number; site: LivePerioSite }[] {
  return fullArchMirroredSequence();
}
